import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { topics } from "@/mocks/topics";
import TopicCard from "@/components/base/TopicCard";

export default function TopicDetail() {
  const { id } = useParams<{ id: string }>();
  const topic = topics.find((item) => item.id === id);

  const related = useMemo(() => {
    if (!topic) return topics.slice(0, 3);
    const sameCategory = topics.filter(
      (item) => item.categoryId === topic.categoryId && item.id !== topic.id,
    );
    const others = topics.filter(
      (item) => item.categoryId !== topic.categoryId && item.id !== topic.id,
    );
    return [...sameCategory, ...others].slice(0, 3);
  }, [topic]);

  const jsonLd = useMemo(() => {
    if (!topic) {
      return [
        {
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "专题未找到 - 瓜集社",
          url: "https://yuntuizq.cn/topics",
          inLanguage: "zh-CN",
        },
      ];
    }
    return [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: topic.title,
        description: topic.summary,
        url: `https://yuntuizq.cn/topics/${topic.id}`,
        datePublished: topic.date,
        articleSection: topic.category,
        keywords: topic.tags.join(","),
        inLanguage: "zh-CN",
        publisher: {
          "@type": "Organization",
          name: "瓜集社",
          url: "https://yuntuizq.cn/",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://yuntuizq.cn/" },
          {
            "@type": "ListItem",
            position: 2,
            name: "专题合集",
            item: "https://yuntuizq.cn/topics",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: topic.title,
            item: `https://yuntuizq.cn/topics/${topic.id}`,
          },
        ],
      },
    ];
  }, [topic]);

  usePageMeta({
    title: topic ? `${topic.title}｜独家吃瓜精选专题 - 瓜集社` : "专题未找到 - 瓜集社",
    description: topic
      ? `${topic.summary}收录于瓜集社独家吃瓜合集目录，按时间线整理看点，一次看完完整专题。`
      : "该专题暂时无法查看，返回合集目录浏览其他内容。",
    keywords: topic ? topic.tags.join(",") : "独家吃瓜合集,独家吃瓜精选",
    path: topic ? `/topics/${topic.id}` : "/topics",
    ogType: "article",
    jsonLd,
  });

  if (!topic) {
    return (
      <section className="mx-auto flex max-w-[1440px] flex-col items-center px-4 py-24 text-center md:px-8">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-600">
          <i className="ri-error-warning-line text-3xl" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这个专题不见了</h1>
        <p className="mt-3 text-sm text-foreground-600">
          它可能已被合并到其他合集，去目录里找找看。
        </p>
        <Link
          to="/topics"
          className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          返回专题合集
        </Link>
      </section>
    );
  }

  return (
    <>
      <section className="border-b border-background-200 bg-background-100/50">
        <div className="mx-auto max-w-[1440px] px-4 py-8 md:px-8 md:py-10">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/topics" className="transition-colors hover:text-primary-600">
              专题合集
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">{topic.category}</span>
          </nav>
        </div>
      </section>

      <article className="mx-auto max-w-[1440px] px-4 py-10 md:px-8 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-8">
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to={`/topics?category=${topic.categoryId}`}
                className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
              >
                {topic.category}
              </Link>
              <span className="text-xs text-foreground-500">
                <i className="ri-calendar-line mr-1" />
                {topic.date}
              </span>
              <span className="text-xs text-foreground-500">
                <i className="ri-time-line mr-1" />
                {topic.readTime}
              </span>
            </div>

            <h1 className="mt-5 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
              {topic.title}
            </h1>
            <p className="mt-5 text-sm leading-relaxed text-foreground-600 md:text-base">
              {topic.summary}
            </p>

            <div className="mt-8 h-64 w-full overflow-hidden rounded-lg border border-background-200 bg-background-200 md:h-96">
              <img
                src={topic.cover}
                alt={`${topic.title}专题配图`}
                className="h-full w-full object-top"
              />
            </div>

            <div className="mt-10 space-y-10">
              {topic.sections.map((section) => (
                <section key={section.heading}>
                  <h2 className="flex items-center gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    <span className="h-5 w-1 rounded-full bg-primary-500" />
                    {section.heading}
                  </h2>
                  <div className="mt-4 space-y-4">
                    {section.paragraphs.map((paragraph) => (
                      <p key={paragraph} className="text-sm leading-8 text-foreground-700 md:text-base">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </section>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
              <span className="text-xs text-foreground-500">相关标签：</span>
              {topic.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/topics?q=${encodeURIComponent(tag)}`}
                  className="rounded-full border border-background-300 px-3 py-1 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  #{tag}
                </Link>
              ))}
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-24 space-y-6">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-timeline-view text-primary-600" />
                  看点时间线
                </h2>
                <ol className="mt-4 space-y-4">
                  {topic.highlights.map((point, index) => (
                    <li key={point.time} className="relative flex gap-3 pl-1">
                      <span className="mt-1 flex flex-col items-center">
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent-100 text-[11px] font-bold text-accent-700">
                          {index + 1}
                        </span>
                        {index < topic.highlights.length - 1 && (
                          <span className="mt-1 h-full w-px flex-1 bg-background-300" />
                        )}
                      </span>
                      <span className="pb-1">
                        <span className="block text-xs font-medium text-primary-700">{point.time}</span>
                        <span className="mt-1 block text-sm leading-relaxed text-foreground-600">
                          {point.text}
                        </span>
                      </span>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="rounded-lg border border-background-200 bg-secondary-50 p-5">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-secondary-900">
                  <i className="ri-information-line" />
                  关于这条合集
                </h2>
                <dl className="mt-4 space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-500">阅读量</dt>
                    <dd className="font-medium text-foreground-900">{topic.views}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-500">整理时间</dt>
                    <dd className="font-medium text-foreground-900">{topic.date}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground-500">所属分类</dt>
                    <dd className="font-medium text-foreground-900">{topic.category}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </article>

      <section className="border-t border-background-200 bg-background-100/50">
        <div className="mx-auto max-w-[1440px] px-4 py-14 md:px-8 md:py-16">
          <div className="flex items-end justify-between">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              相关合集推荐
            </h2>
            <Link
              to="/topics"
              className="whitespace-nowrap text-sm font-medium text-primary-700 transition-colors hover:text-primary-600"
            >
              查看全部
              <i className="ri-arrow-right-line ml-1" />
            </Link>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((item) => (
              <TopicCard key={item.id} topic={item} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}